import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { HubConnectionBuilder } from "@microsoft/signalr";
import { useTenant } from './TenantProvider';
import { useAuth } from './AuthProvider';
import { getActiveChatAsync, startChatAsync, endChatAsync, requestAgentAsync, optOutTranscriptAsync } from '../apis/session';
import { ChatHubMethod, MessageType, ChatClient, MessageRole } from '../constants';
import Avatar from '../components/Avatar';
import ConversationEndedDivider from '../components/ConversationEndedDivider';
import AgentJoinedDivider from '../components/AgentJoinedDivider';

import { toggleWidget, toggleMsgLoader, addUserMessage, addResponseMessage, dropMessages } from 'tls-chat-widget';
import { getInitial } from '../utils';
import { playNewMessageSound } from "../utils/sounds";

const ChatContext = createContext(null);

function useChatHook() {
    const { token, userName, setIsLoading, setRequireAuthorization } = useAuth();
    const { botName, closingMessage } = useTenant();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [activeChatId, setActiveChatId] = useState(undefined);

    const [chatEndedMessage, setChatEndedMessage] = useState(undefined);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const [connection, setConnection] = useState(null);

    const [botOrAgentName, setBotOrAgentName] = useState(botName);

    const [hasRequestedAgent, setHasRequestedAgent] = useState(false);

    const [hasOptedOutTranscript, setHasOptedOutTranscript] = useState(false);

    useEffect(() => {
        setBotOrAgentName(botName);
    }, [botName]);

    useEffect(() => {
        //when the user logs out, stop the connection and remove all messages.
        if (!connection || token) return;
        connection.stop();
        setConnection(null);
        dropMessages();
    }, [token, connection]);

    useEffect(() => {
        if (!token || connection?._connectionStarted) return;

        async function createHubConnection() {

            const excludedTypes = [MessageType.TOOL_CALL, MessageType.AUTHORISATION];
            const excludedRoles = [MessageRole.FUNCTION, MessageRole.SYSTEM];

            const hub = new HubConnectionBuilder()
                .withUrl(`${process.env.REACT_APP_KANIS_API_URL}/hubs/chat/`, { accessTokenFactory: () => token })
                .withAutomaticReconnect()
                .build();

            hub.on(ChatHubMethod.RECEIVE_MESSAGES, (messages) => {
                messages.forEach(message => {
                    if (excludedTypes.includes(message.type) || excludedRoles.includes(message.role)) {
                        return;
                    }

                    if (message.role === MessageRole.USER) {
                        addUserMessage(message.content, message.id);
                        return;
                    }

                    setBotOrAgentName(message.senderDisplayName);

                    if (message.chatEndedOn) {
                        addResponseMessage({ component: ConversationEndedDivider, props: { chatEndedOn: message.chatEndedOn } }, message.id);
                        return;
                    }

                    addResponseMessage(message.content, message.id, message.role === MessageRole.BOT ? null : <Avatar initial={getInitial(message.senderDisplayName)} />);
                });
            });

            hub.on(ChatHubMethod.RECEIVE_MESSAGE, (message) => {
                if (excludedTypes.includes(message.type) || excludedRoles.includes(message.role)) {
                    return;
                }

                if (message?.role === MessageRole.USER) {
                    addUserMessage(message.content, message.id);
                    return;
                }

                setBotOrAgentName(message.senderDisplayName);

                if (message.chatEndedOn) {
                    setChatEndedMessage(closingMessage);
                    setActiveChatId(undefined);

                    if (message.intendedTarget == null || message.intendedTarget == ChatClient.WIDGET) {
                        // Server intends for us to display this 'chat ended' message
                        addResponseMessage({ component: ConversationEndedDivider, props: { chatEndedOn: message.chatEndedOn } }, message.id);
                    }                    
                    return;
                }

                if (message.type == MessageType.AGENT_JOINED) {
                    setHasRequestedAgent(true);
                    addResponseMessage({ component: AgentJoinedDivider, props: { content: `You are now chatting with ${message.senderDisplayName}` } }, message.id);
                    return;
                }

                playNewMessageSound();

                addResponseMessage(message.content, message.id, message.role === MessageRole.BOT ? null : <Avatar initial={getInitial(message.senderDisplayName)} />);
            });

            hub.on(ChatHubMethod.BOT_IS_TYPING, () => {
                toggleMsgLoader();
            });

            hub.on(ChatHubMethod.UPDATE_SESSION, ({ chatId, chatEndedAt }) => {
                if (!chatEndedAt)
                    setActiveChatId(chatId);
            });

            hub.on(ChatHubMethod.AUTHORIZATION_REQUIRED, (authIsRequired) => {
                setRequireAuthorization(authIsRequired);
            });

            hub.onclose(() => {
                setConnection(null);
            });

            await hub.start();
            setConnection(hub);
        }

        try {
            createHubConnection();
        } catch (e) {
            console.log(e);
        }

        return () => connection?.stop();
    }, [token, connection]);

    useEffect(() => {
        async function fetchActiveChat() {
            if (!token) return;

            try {
                setIsLoading(true);
                const { chatId, hasRequestedAgent, optOutTranscript } = await getActiveChatAsync();
                setActiveChatId(chatId);
                setHasRequestedAgent(hasRequestedAgent);
                setHasOptedOutTranscript(optOutTranscript);
            } catch (e) {
                console.log(e);
            }
            finally{
                setIsLoading(false);
            }
        }
        fetchActiveChat();
    }, [token, getActiveChatAsync]);

    const startChat = useCallback(async () => {
        if (!token) return;

        try {
            setIsLoading(true);
            const { chatId, hasRequestedAgent, optOutTranscript }  = await startChatAsync();
            setActiveChatId(chatId);
            setHasRequestedAgent(hasRequestedAgent);
            setHasOptedOutTranscript(optOutTranscript);
        } catch (e) {
            console.log(e);
        }
        finally{
            setIsLoading(false);
        }

    }, [token, startChatAsync]);

    const endChat = useCallback(async () => {
        if (!token || !activeChatId) return;

        try {
            setIsLoading(true);
            const chatId = activeChatId;
            setActiveChatId(undefined);
            await endChatAsync(chatId);
        } catch (e) {
            console.log(e);
        }
        finally {
            setIsLoading(false);
        }
    }, [token, activeChatId, endChatAsync]);

    const requestAgent = useCallback(async () => {
        if (!token || !activeChatId) return;

        try {
            await requestAgentAsync(activeChatId);
        } catch (e) {
            console.log(e);
        }
    }, [token, activeChatId, endChatAsync]);

    const optOutTranscript = useCallback(async () => {
        if (!token || !activeChatId) return;

        try {
            var optOut = !hasOptedOutTranscript;
            await optOutTranscriptAsync(optOut);
            setHasOptedOutTranscript(optOut);
        } catch (e) {
            console.log(e);
        }
    }, [token, activeChatId, optOutTranscriptAsync, setHasOptedOutTranscript, hasOptedOutTranscript]);

    const widgetIsTyping = useCallback(async (messagePreview) => {
        if (!token || !activeChatId || !connection) return;

        try {
            await connection.invoke(ChatHubMethod.WIDGET_IS_TYPING, activeChatId, userName, messagePreview);
        } catch (e) {
            console.log(e);
        }
    }, [token, activeChatId, connection, userName]);

    return {
        isChatOpen,
        setIsChatOpen,
        activeChatId,
        hasActiveChat: !!activeChatId,
        startChat,
        endChat,
        setActiveChatId,
        requestAgent,
        toggleWidget,
        chatEndedMessage,
        setChatEndedMessage,
        showCloseConfirm,
        setShowCloseConfirm,
        botOrAgentName,
        widgetIsTyping,
        hasRequestedAgent,
        hasOptedOutTranscript,
        optOutTranscript
    };
}

export const useChat = () => {
    const contextValue = useContext(ChatContext);
    if (contextValue === null) throw Error('ChatContext has not been Provided!');
    return contextValue;
};
export default function ChatProvider({ children }) {
    const chat = useChatHook();
    return (
        <ChatContext.Provider value={chat}>
            {children}
        </ChatContext.Provider>
    );
}

ChatProvider.propTypes = {
    children: PropTypes.node,
};